import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'

import { SegmentOptionGroup } from '../helpers/segmentFilterOptions'

const POPOVER_WIDTH = 320
const LIST_MAX_HEIGHT = 280

interface SegmentOptionPopoverProps {
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  // Already scoped, pruned and ordered by the caller — this component only
  // searches within, groups and toggles.
  groups: SegmentOptionGroup[]
  selectedIds: readonly string[]
  // Commits the whole level at once, with what the picker was left holding.
  onApply: (ids: string[]) => void
  searchPlaceholder: string
  ariaLabel: string
}

// Order-insensitive: toggling an option off and back on reorders the draft, which
// is still the same selection and must not rewrite the URL (and refetch).
function sameSelection(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((id) => set.has(id))
}

/**
 * The "+ Add" picker for one level of the segment filter: a searchable checkbox
 * list of the options that level currently offers. Options are grouped under
 * their parent when more than one parent is in scope, so two same-named
 * subgenres from different genres stay distinguishable.
 *
 * Ticks are staged and only committed when the reader presses Done; clicking
 * away or pressing Escape discards them. Applying each
 * click straight away moved the ground under the reader: every pick added a
 * chip to the row behind, which shifted the "+ Add" chip this popover is
 * anchored to (and unmounted it outright once a row ran out of options), and
 * re-filtered the whole page mid-selection. Staging also means picking four
 * categories costs one refetch instead of four.
 */
export const SegmentOptionPopover: React.FC<SegmentOptionPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  groups,
  selectedIds,
  onApply,
  searchPlaceholder,
  ariaLabel,
}) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  // `null` until the reader touches something, so the list mirrors what is
  // actually applied without an effect to sync the two.
  const [draftIds, setDraftIds] = useState<string[] | null>(null)

  const editedIds = draftIds ?? selectedIds
  const selectedSet = useMemo(() => new Set(editedIds), [editedIds])

  const toggleDraft = useCallback(
    (id: string) => {
      setDraftIds((prev) => {
        const current = prev ?? selectedIds
        return current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
      })
      // `selectedIds` is stable while the picker is open — nothing commits until it
      // closes — so seeding from it inside the updater cannot go stale.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [selectedIds]
  )

  const commitAndClose = useCallback(() => {
    if (draftIds && !sameSelection(draftIds, selectedIds)) onApply(draftIds)
    setDraftIds(null)
    onClose()
  }, [draftIds, selectedIds, onApply, onClose])

  // Clicking away or pressing Escape cancels: the staged ticks are dropped and
  // nothing is applied. Only the Done button commits.
  const cancelAndClose = useCallback(() => {
    setDraftIds(null)
    onClose()
  }, [onClose])

  // Matches the parent's name too, so typing "Casino" finds the subgenres under it.
  const visibleGroups = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase()
    if (!needle) return groups
    const matches = (text: string | null | undefined) => Boolean(text && text.toLocaleLowerCase().includes(needle))
    return groups
      .map((group) => ({ ...group, options: group.options.filter((option) => matches(option.name) || matches(option.parentName)) }))
      .filter((group) => group.options.length > 0)
  }, [groups, query])

  const showGroupHeaders = groups.length > 1
  const selectedCount = editedIds.length

  // Reset the search and any leftover draft each time the popover is opened
  // afresh, rather than on close, so the exit transition doesn't flash the
  // unfiltered list.
  const handleTransitionEnter = () => {
    setQuery('')
    setDraftIds(null)
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={cancelAndClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      TransitionProps={{ onEnter: handleTransitionEnter }}
      PaperProps={{ sx: { width: POPOVER_WIDTH, mt: 0.5 }, role: 'dialog', 'aria-label': ariaLabel }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          inputProps={{ 'aria-label': searchPlaceholder }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <List dense disablePadding sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
        {visibleGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
            {t('market-analytics:segment_filter_no_matches')}
          </Typography>
        ) : null}
        {visibleGroups.map((group) => (
          <React.Fragment key={group.parentId ?? 'root'}>
            {showGroupHeaders ? (
              <ListSubheader
                disableSticky
                disableGutters
                sx={{
                  mx: 1.5,
                  mb: 0.5,
                  pt: 1,
                  pb: 0.5,
                  lineHeight: 1.6,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                  bgcolor: 'transparent',
                  // Same dashed seam the card uses between its rows, inset to the
                  // text edge so it reads as an underline for the group, not a
                  // divider across the whole list.
                  borderBottom: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                {group.parentName} · {group.options.length}
              </ListSubheader>
            ) : null}
            {group.options.map((option) => {
              const selected = selectedSet.has(option.id)
              return (
                <ListItemButton
                  key={option.id}
                  dense
                  role="checkbox"
                  aria-checked={selected}
                  selected={selected}
                  onClick={() => toggleDraft(option.id)}
                  // Selected rows tint edge to edge, so a run of them reads as one band
                  // rather than a stack of floating pills.
                  sx={(theme) => ({
                    py: 0.25,
                    pl: 1,
                    pr: 2,
                    '&.Mui-selected, &.Mui-selected:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.dark,
                    },
                  })}
                >
                  <Checkbox edge="start" size="small" checked={selected} tabIndex={-1} disableRipple inputProps={{ 'aria-hidden': true }} sx={{ py: 0.5 }} />
                  <ListItemText
                    primary={option.name}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { fontWeight: selected ? 'bold' : 'normal' } }}
                  />
                </ListItemButton>
              )
            })}
          </React.Fragment>
        ))}
      </List>

      <Divider sx={{ mt: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('market-analytics:segment_filter_selected_count', { count: selectedCount })}
        </Typography>
        <Button size="small" variant="contained" color="secondary" onClick={commitAndClose}>
          {t('common:done')}
        </Button>
      </Box>
    </Popover>
  )
}
